import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Dropdown, Message, Segment } from 'semantic-ui-react'

import { fetchBillingInvoiceStats } from 'actions/billing'
import { getBillingInvoices } from 'selectors/billing'

const PERIOD_OPTIONS = [
  { key: 'this_month',          value: 'this_month',          text: 'This month' },
  { key: 'last_month',          value: 'last_month',          text: 'Last month' },
  { key: 'this_quarter',        value: 'this_quarter',        text: 'This quarter' },
  { key: 'last_quarter',        value: 'last_quarter',        text: 'Last quarter' },
  { key: 'this_year',           value: 'this_year',           text: 'This year' },
  { key: 'last_year',           value: 'last_year',           text: 'Last year' },
  { key: 'this_financial_year', value: 'this_financial_year', text: 'This financial year (from 1 Jul)' },
  { key: 'last_financial_year', value: 'last_financial_year', text: 'Last financial year (from 1 Jul)' }
]

const formatAmount = (amount) =>
  `€ ${Number(amount).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

const formatMonth = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

const countLabel = (count) => `${count} invoice${count === 1 ? '' : 's'}`

const MUTED = 'rgba(0, 0, 0, 0.55)'

// One figure: a quiet label, the amount in a light weight, a small note below.
function Tile ({ label, value, note, children }) {
  return (
    <div>
      <div style={{ color: MUTED, marginBottom: 6 }}>{label}{children}</div>
      <div style={{ fontSize: '1.9rem', fontWeight: 300, lineHeight: 1.2 }}>{value}</div>
      <div style={{ color: MUTED, fontSize: '0.9em', marginTop: 6, minHeight: '1.2em' }}>{note}</div>
    </div>
  )
}

// Totals above the invoice list. They come from the server (not from the
// loaded cards, which are capped), and are refreshed when an invoice changes
// status (e.g. marked paid), which is what `invoicesKey` detects.
function BillingInvoiceStats ({ fetchStats, invoicesKey }) {
  const [period, setPeriod] = useState('this_financial_year')
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchStats(period)
      .then((result) => { if (!cancelled) { setStats(result); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e) })
    return () => { cancelled = true }
  }, [period, invoicesKey])

  if (error) return <Message negative>Could not load invoice totals: {error.message}</Message>

  return (
    <Segment loading={!stats} style={{ padding: '1.75em 2em', marginBottom: '2em' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '2em 3em' }}>
        <Tile label="Outstanding (incl. VAT)"
              value={stats ? formatAmount(stats.outstanding.amount_incl_vat) : '—'}
              note={stats && `${countLabel(stats.outstanding.count)} issued or reminded`}/>
        <Tile label="Invoiced (excl. VAT) ·"
              value={stats ? formatAmount(stats.invoiced.amount_excl_vat) : '—'}
              note={stats && `${formatDate(stats.from)} – ${formatDate(stats.to)} · ${countLabel(stats.invoiced.count)}`}>
          {' '}
          <Dropdown inline options={PERIOD_OPTIONS} value={period}
                    onChange={(e, { value }) => setPeriod(value)}/>
        </Tile>
        <Tile label="Estimated income per month (excl. VAT)"
              value={stats ? formatAmount(stats.forecast.monthly_estimate) : '—'}
              note={stats && `${formatMonth(stats.forecast.from)} · about ${formatAmount(stats.forecast.annual_estimate)} per year at this rate`}/>
      </div>
    </Segment>
  )
}

const mapStateToProps = (state) => ({
  invoicesKey: getBillingInvoices(state).map((inv) => `${inv.id}:${inv.status}`).join(',')
})

const mapDispatchToProps = (dispatch) => ({
  fetchStats: (period) => dispatch(fetchBillingInvoiceStats(period))
})

export default connect(mapStateToProps, mapDispatchToProps)(BillingInvoiceStats)
